import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { getServiceInstanceIdFromSearch, resolveActiveServiceInstanceId } from '@/lib/service-instance'
import { orgServiceInstanceLightQueryKey, orgTreeApi, type OrgTreeTimezone } from '@/services/org-tree'
import { useAuthStore } from '@/stores/auth-store'
import { orgWallClockToApiDateTime, toOrgWallClockDateTimeLocal } from '@/lib/workday-entry-schedule'
import { formatDateForLocale, formatDateTimeForLocale } from '@/lib/org-date-locale'
import { formatDate } from '@/lib/utils'

/**
 * Helpers for showing server datetimes in the tenant's configured timezone.
 *
 * Server timezone assumption: the API sends naive datetimes (no "Z"/offset, e.g.
 * "07-10-2026 12:22:03") in UTC. The project defines no server-timezone constant,
 * so this follows the same rule the commerce order date parsing already uses.
 */

const DMY_PATTERN = /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

type ZonedParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

const pad = (value: number) => String(value).padStart(2, '0')

const toDateKey = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`

// Intl.DateTimeFormat construction is costly; long job lists reuse one instance per locale+options.
const formatterCache = new Map<string, Intl.DateTimeFormat>()

const getCachedFormatter = (locale: string | undefined, options: Intl.DateTimeFormatOptions) => {
  const cacheKey = `${locale ?? ''}|${JSON.stringify(options)}`
  let formatter = formatterCache.get(cacheKey)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    formatterCache.set(cacheKey, formatter)
  }
  return formatter
}

/** Parses ISO strings and "DD-MM-YYYY HH:mm:ss" strings; naive values are treated as UTC. */
export function parseServerDate(value?: string | null): Date | null {
  if (!value) {
    return null
  }

  const trimmed = String(value).trim()
  if (!trimmed) {
    return null
  }

  const match = trimmed.match(DMY_PATTERN)
  if (match) {
    const [, day, month, year, hour = '0', minute = '0', second = '0'] = match
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second)))
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const hasExplicitTimeZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed)
  const hasClockTime = /\d{2}:\d{2}/.test(trimmed)
  const normalized = trimmed.includes(' ') ? trimmed.replace(' ', 'T') : trimmed
  const parsed = hasClockTime && !hasExplicitTimeZone
    ? new Date(`${normalized}Z`)
    : new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Returns the IANA timezone when it is valid for Intl, otherwise null. */
export function getValidTimeZone(timeZone?: string | null): string | null {
  const candidate = String(timeZone ?? '').trim()
  if (!candidate) {
    return null
  }

  try {
    getCachedFormatter('en-US', { timeZone: candidate })
    return candidate
  } catch {
    return null
  }
}

/**
 * Resolves the tenant's IANA timezone from the service instance `time_zone_id`/`time_zone`
 * reference, matching the header's lookup against the timezone list.
 */
export function resolveTenantTimeZone(
  reference: string | number | null | undefined,
  timezones?: OrgTreeTimezone[] | null,
): string | null {
  if (reference == null || reference === '') {
    return null
  }

  const referenceText = String(reference).trim()
  const matched = (timezones || []).find((timezone) => (
    String(timezone.id) === referenceText || String(timezone.timezone || '').trim() === referenceText
  ))
  return getValidTimeZone(matched?.timezone || (referenceText.includes('/') ? referenceText : null))
}

/** Formats a date in the tenant timezone; an invalid/missing timezone keeps the runtime default. */
export function formatInTenantTZ(
  date: Date,
  tenantTimeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const timeZone = getValidTimeZone(tenantTimeZone)
  return getCachedFormatter(locale, timeZone ? { ...options, timeZone } : options).format(date)
}

const getZonedParts = (date: Date, timeZone: string): ZonedParts => {
  const parts = getCachedFormatter('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? 0)
  return { year: read('year'), month: read('month'), day: read('day'), hour: read('hour') % 24, minute: read('minute') }
}

/** Today's and tomorrow's calendar keys (YYYY-MM-DD) in the tenant timezone, or null without a valid timezone. */
export function getNowInTenantTZ(tenantTimeZone?: string | null): { now: Date; todayKey: string; tomorrowKey: string } | null {
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!timeZone) {
    return null
  }

  const now = new Date()
  const { year, month, day } = getZonedParts(now, timeZone)
  const tomorrow = new Date(Date.UTC(year, month - 1, day + 1))
  return {
    now,
    todayKey: toDateKey(year, month, day),
    tomorrowKey: toDateKey(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate()),
  }
}

/**
 * Converts a server datetime string to tenant wall-clock time as "YYYY-MM-DDTHH:mm" (for raw
 * date renderers) plus its tenant calendar key. Date-only values are not shifted.
 * Returns null without a valid timezone or a parseable value.
 */
export function toTenantWallClock(
  value: string | null | undefined,
  tenantTimeZone?: string | null,
): { value: string; dateKey: string } | null {
  const timeZone = getValidTimeZone(tenantTimeZone)
  const trimmed = String(value ?? '').trim()
  if (!timeZone || !trimmed) {
    return null
  }

  const dmyDateOnly = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmyDateOnly || ISO_DATE_ONLY_PATTERN.test(trimmed)) {
    const dateKey = dmyDateOnly ? `${dmyDateOnly[3]}-${dmyDateOnly[2]}-${dmyDateOnly[1]}` : trimmed
    return { value: dateKey, dateKey }
  }

  const parsed = parseServerDate(trimmed)
  if (!parsed) {
    return null
  }

  const { year, month, day, hour, minute } = getZonedParts(parsed, timeZone)
  const dateKey = toDateKey(year, month, day)
  return { value: `${dateKey}T${pad(hour)}:${pad(minute)}`, dateKey }
}

/** Calendar key (YYYY-MM-DD) of an instant in the tenant timezone, or null without a valid timezone. */
export function toTenantDateKey(date: Date, tenantTimeZone?: string | null): string | null {
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!timeZone || !Number.isFinite(date.getTime())) {
    return null
  }

  const { year, month, day } = getZonedParts(date, timeZone)
  return toDateKey(year, month, day)
}

const normalizeServiceInstanceId = (value?: string | null) => {
  const text = String(value ?? '').trim()
  return text || null
}

/**
 * Service instance whose timezone is the tenant timezone: the explicit argument, then the URL
 * `service_instance_id`, then the auth-store scope the app header starts from.
 */
export function resolveTenantServiceInstanceId({
  explicitServiceInstanceId,
  search,
  scopedServiceInstanceId,
}: {
  explicitServiceInstanceId?: string | null
  search?: string
  scopedServiceInstanceId?: string | null
}): string | null {
  return normalizeServiceInstanceId(explicitServiceInstanceId)
    ?? normalizeServiceInstanceId(search ? getServiceInstanceIdFromSearch(search) : null)
    ?? normalizeServiceInstanceId(scopedServiceInstanceId)
}

/** A tenant timezone is usable on write paths only when it is a valid IANA zone; no browser fallback. */
export function toRequiredTenantTimeZone(timeZone?: string | null): { timeZone: string | null; isReady: boolean } {
  const valid = getValidTimeZone(timeZone)
  return { timeZone: valid, isReady: valid !== null }
}

// Same keys, query functions and stale times as the app header, so React Query shares one cache entry.
const SERVICE_INSTANCE_STALE_TIME_MS = 5 * 60 * 1000
const TIMEZONE_LIST_STALE_TIME_MS = 30 * 60 * 1000

/**
 * Tenant IANA timezone from the service-instance + timezone-list queries the layout/header
 * already load (same source as the currency symbol). Served from the cache when present; on a
 * cold cache it runs those same queries once. Returns null when unavailable/invalid.
 */
export function useTenantTimeZone(serviceInstanceId?: string | null): string | null {
  const location = useLocation()
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)

  const resolvedServiceInstanceId = resolveTenantServiceInstanceId({
    explicitServiceInstanceId: serviceInstanceId,
    search: location.search,
    scopedServiceInstanceId: resolveActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId,
      token: accessToken,
    }),
  })

  const serviceInstanceQuery = useQuery({
    queryKey: orgServiceInstanceLightQueryKey(String(resolvedServiceInstanceId)),
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(resolvedServiceInstanceId)),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: SERVICE_INSTANCE_STALE_TIME_MS,
  })
  const timezonesQuery = useQuery({
    queryKey: ['header-timezone-list'],
    queryFn: () => orgTreeApi.listTimezones(),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: TIMEZONE_LIST_STALE_TIME_MS,
  })

  return resolveTenantTimeZone(
    serviceInstanceQuery.data?.time_zone_id ?? serviceInstanceQuery.data?.time_zone,
    timezonesQuery.data,
  )
}

/**
 * Tenant timezone for write paths. `isReady` is true only for a valid IANA zone; this never falls
 * back to the browser timezone, so callers must disable submit while `isReady` is false.
 */
export function useRequiredTenantTimeZone(serviceInstanceId?: string | null): { timeZone: string | null; isReady: boolean } {
  return toRequiredTenantTimeZone(useTenantTimeZone(serviceInstanceId))
}

const WALL_CLOCK_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** Tenant "now" as a datetime-local value ("YYYY-MM-DDTHH:mm"); empty without a valid tenant timezone. */
export function getTenantNowInputValue(tenantTimeZone?: string | null, now: Date = new Date()): string {
  const timeZone = getValidTimeZone(tenantTimeZone)
  return timeZone ? toOrgWallClockDateTimeLocal(now, timeZone) : ''
}

/** Stored server instant as a tenant datetime-local value; empty when missing, unparseable or without a timezone. */
export function toTenantInputValue(value: string | null | undefined, tenantTimeZone?: string | null): string {
  // Date-only values are not instants and cannot seed a datetime-local input.
  const wallClock = toTenantWallClock(value, tenantTimeZone)?.value ?? ''
  return wallClock.includes('T') ? wallClock : ''
}

/**
 * Tenant datetime-local value as an ISO UTC instant ("2026-10-08T15:00:00.000Z").
 * Undefined when empty, malformed or without a valid tenant timezone, so it never reads the value
 * in the browser timezone.
 */
export function tenantInputToIso(value: string | null | undefined, tenantTimeZone?: string | null): string | undefined {
  const timeZone = getValidTimeZone(tenantTimeZone)
  const trimmed = String(value ?? '').trim()
  if (!timeZone || !WALL_CLOCK_INPUT_PATTERN.test(trimmed)) {
    return undefined
  }
  return orgWallClockToApiDateTime(trimmed, timeZone)
}

/**
 * Like tenantInputToIso, but when the input still shows the seeded stored instant, returns that
 * instant unchanged (as toISOString) so an unedited save does not drop the seconds the minute-precision
 * input cannot show.
 */
export function tenantInputToIsoKeepingSource(
  value: string | null | undefined,
  tenantTimeZone: string | null | undefined,
  sourceInstant: string | null | undefined,
): string | undefined {
  const source = sourceInstant ? parseServerDate(sourceInstant) : null
  const seeded = source ? toTenantInputValue(sourceInstant, tenantTimeZone) : ''
  if (source && seeded && seeded === String(value ?? '').trim()) {
    return source.toISOString()
  }
  return tenantInputToIso(value, tenantTimeZone)
}

const DATE_ONLY_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }

/**
 * Formats a date-only value ("YYYY-MM-DD", or a "DD-MM-YYYY" date) as a calendar date, never shifted
 * by any timezone. The default output matches `formatDate` in lib/utils (e.g. "Oct 8, 2026").
 * Returns null for anything that is not a plain date, so callers can keep their existing formatting.
 */
export function formatDateOnly(
  value: string | null | undefined,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = DATE_ONLY_FORMAT_OPTIONS,
): string | null {
  const trimmed = String(value ?? '').trim()
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const dmy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  const parts = iso ? [iso[1], iso[2], iso[3]] : dmy ? [dmy[3], dmy[2], dmy[1]] : null
  if (!parts) {
    return null
  }
  const [year, month, day] = parts.map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  if (Number.isNaN(date.getTime()) || date.getUTCDate() !== day) {
    return null
  }
  return getCachedFormatter(locale, { ...options, timeZone: 'UTC' }).format(date)
}

/**
 * Calendar day key (YYYY-MM-DD) of a server value in the tenant timezone. Date-only values
 * ("YYYY-MM-DD" or "DD-MM-YYYY") are returned as-is, never shifted; instants (ISO or UTC
 * "DD-MM-YYYY HH:mm:ss") use their tenant day. Null without a valid timezone or a parseable value.
 */
export function toTenantDayKey(value: string | null | undefined, tenantTimeZone?: string | null): string | null {
  const trimmed = String(value ?? '').trim()
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!trimmed || !timeZone) {
    return null
  }
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) {
    return trimmed
  }
  const dmy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/)
  if (dmy) {
    return `${dmy[3]}-${dmy[2]}-${dmy[1]}`
  }
  const parsed = parseServerDate(trimmed)
  return parsed ? toTenantDateKey(parsed, timeZone) : null
}

/**
 * Date for a server value. Without a tenant timezone this is exactly `formatDateForLocale(value, locale,
 * fallback)`. With one, date-only values are never shifted and instants (ISO or UTC
 * "DD-MM-YYYY HH:mm:ss") show their tenant date.
 */
export function formatServerDateForLocale(
  value: string | null | undefined,
  locale: string,
  tenantTimeZone?: string | null,
  fallback = '-',
): string {
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!timeZone) {
    return formatDateForLocale(value, locale, fallback)
  }
  const dateOnly = formatDateOnly(value, locale)
  if (dateOnly) {
    return dateOnly
  }
  const instant = value ? parseServerDate(value) : null
  return instant ? formatDateForLocale(instant, locale, fallback, timeZone) : formatDateForLocale(value, locale, fallback)
}

/**
 * Date and time for a server value. Without a tenant timezone this is exactly
 * `formatDateTimeForLocale(value, locale, fallback)`. With one, instants (ISO or UTC
 * "DD-MM-YYYY HH:mm:ss") show in tenant time and date-only values show as an unshifted date.
 */
export function formatServerDateTimeForLocale(
  value: string | null | undefined,
  locale: string,
  tenantTimeZone?: string | null,
  fallback = '-',
): string {
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!timeZone) {
    return formatDateTimeForLocale(value, locale, fallback)
  }
  const dateOnly = formatDateOnly(value, locale)
  if (dateOnly) {
    return dateOnly
  }
  const instant = value ? parseServerDate(value) : null
  return instant ? formatDateTimeForLocale(instant, locale, fallback, timeZone) : formatDateTimeForLocale(value, locale, fallback)
}

/**
 * "Oct 8, 2026" for a server value. Without a tenant timezone this is exactly `formatDate(value)` from
 * utils. With one, date-only values are never shifted and instants (ISO or UTC "DD-MM-YYYY HH:mm:ss")
 * show their tenant date.
 */
export function formatServerDate(value: string | Date, tenantTimeZone?: string | null): string {
  const timeZone = getValidTimeZone(tenantTimeZone)
  if (!timeZone) {
    return formatDate(value)
  }
  if (typeof value === 'string') {
    const dateOnly = formatDateOnly(value)
    if (dateOnly) {
      return dateOnly
    }
  }
  const instant = typeof value === 'string' ? parseServerDate(value) : value
  return instant ? formatDate(instant, { timeZone }) : formatDate(value)
}
